/**
 * Formats a number to Dutch currency format
 * @param {number|string} value - The amount to format
 * @param {boolean} showSymbol - Show euro symbol (default: true)
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted amount
 */
export const formatCurrency = (value, showSymbol = true, decimals = 2) => {
  const number = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(number)) return showSymbol ? '€ 0,00' : '0,00';
  
  const formatter = new Intl.NumberFormat('nl-NL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    ...(showSymbol && { 
      style: 'currency', 
      currency: 'EUR' 
    })
  });
  
  return formatter.format(number);
};

/**
 * Formats a number to Dutch number format (without currency)
 * @param {number|string} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted number
 */
export const formatNumber = (value, decimals = 2) => {
  return formatCurrency(value, false, decimals);
};

/**
 * Formats an amount without decimals
 * @param {number|string} value - The amount to format
 * @returns {string} Formatted amount without decimals
 */
export const formatWholeAmount = (value) => {
  return formatCurrency(value, true, 0);
};

/**
 * Parse Dutch formatted number/amount back to float
 * @param {string} value - The formatted number/amount
 * @returns {number} Numeric value
 */
export const parseCurrency = (value) => {
  if (typeof value !== 'string') return value;

  let cleaned = value.replace(',', '.');   // Replace comma with dot
  
  return parseFloat(cleaned) || 0;
};

/**
 * Formats a percentage with automatic decimal handling
 * @param {number|string} value - The percentage (e.g. 21 for 21%, 21.5 for 21.5%)
 * @param {number} maxDecimals - Maximum number of decimal places (default: 2)
 * @returns {string} Formatted percentage
 */
export const formatPercentage = (value, maxDecimals = 2) => {
  if (value === null || value === undefined || value === '') return '-';

  const number = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(number)) return '0%';
  
  // Check if the number has decimal places
  const hasDecimals = number % 1 !== 0;
  
  // Use appropriate decimal places
  const decimals = hasDecimals ? maxDecimals : 0;
  
  return `${formatNumber(number, decimals)}%`;
};